import React, { useState } from 'react';
import { Box, Button, Chip, Stack, Typography, CircularProgress, Link } from '@mui/material';
import { supabase } from './supabaseClient';

/**
 * The references a stylist gave at onboarding.
 *
 * StylistOnboardingView has collected these into stylist_references since it
 * was written, and the table's read policy has always allowed an admin — but
 * nothing in this console ever read it, so a reference a stylist supplied could
 * not be looked at by the person vetting her. That is the whole gap this fills.
 *
 * Loaded on demand rather than with the list: references matter when someone is
 * actually being vetted, and fetching them for every stylist on screen would be
 * a lot of rows nobody asked for.
 */
export default function StylistReferences({ stylistPhone, brand, ink }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);

  const digits = (v) => String(v || '').replace(/\D/g, '');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Onboarding writes userProfile.phoneNumber, which is the same value this
      // page reads off Profiles, so an exact match is the common case. The
      // variants cover rows written before the + settled down — this app has a
      // long history of the same number stored two ways.
      const bare = digits(stylistPhone);
      const variants = [...new Set([stylistPhone, bare, `+${bare}`, `+1${bare}`, bare.replace(/^1/, '')])]
        .filter(Boolean);

      const { data, error: queryError } = await supabase
        .from('stylist_references')
        .select('id, stylist_phone_number, referee_name, relationship, referee_email, referee_phone, checked_at, checked_by, created_at')
        .in('stylist_phone_number', variants)
        .order('created_at', { ascending: true });
      if (queryError) throw queryError;

      // Belt and braces: the .in() above cannot express "same digits", so a
      // format nobody anticipated still gets caught here rather than silently
      // showing a stylist no references.
      setRows((data || []).filter((r) => digits(r.stylist_phone_number) === bare));
    } catch (err) {
      setError(err.message || 'Could not load references');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const markChecked = async (row) => {
    setSaving(row.id);
    try {
      const { data: me } = await supabase.auth.getUser();
      const who = me?.user?.email || 'admin';
      const { error: updateError } = await supabase
        .from('stylist_references')
        .update({ checked_at: new Date().toISOString(), checked_by: who })
        .eq('id', row.id);
      if (updateError) throw updateError;
      setRows((current) =>
        current.map((r) =>
          r.id === row.id ? { ...r, checked_at: new Date().toISOString(), checked_by: who } : r
        )
      );
    } catch (err) {
      setError(err.message || 'Could not mark that reference checked');
    } finally {
      setSaving(null);
    }
  };

  if (rows === null) {
    return (
      <Button size="small" onClick={load} disabled={loading} sx={{ mb: 1 }}>
        {loading ? 'Loading references…' : 'Show references'}
      </Button>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ color: ink?.[500], display: 'block', mb: 0.5 }}>
        REFERENCES
      </Typography>

      {loading && <CircularProgress size={18} />}

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {!loading && rows.length === 0 && (
        <Typography variant="body2" sx={{ color: ink?.[500] }}>
          None given. References are optional at onboarding, so this is not a failure — it
          just means there is nothing to check.
        </Typography>
      )}

      <Stack spacing={1}>
        {rows.map((r) => (
          <Box
            key={r.id}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${r.checked_at ? 'transparent' : 'rgba(0,0,0,0.12)'}`,
              bgcolor: r.checked_at ? 'rgba(76,175,80,0.08)' : 'transparent',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 600 }}>{r.referee_name}</Typography>
              {r.relationship && <Chip size="small" variant="outlined" label={r.relationship} />}
              {r.checked_at ? (
                <Chip
                  size="small"
                  color="success"
                  label={`Checked by ${r.checked_by || 'admin'} · ${new Date(r.checked_at).toLocaleDateString()}`}
                />
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving === r.id}
                  onClick={() => markChecked(r)}
                >
                  {saving === r.id ? 'Saving…' : 'Mark checked'}
                </Button>
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: ink?.[500], mt: 0.5 }}>
              {r.referee_email ? (
                <Link href={`mailto:${r.referee_email}`} sx={{ color: brand?.goldDeep }}>
                  {r.referee_email}
                </Link>
              ) : r.referee_phone ? (
                <Link href={`tel:${r.referee_phone}`} sx={{ color: brand?.goldDeep }}>
                  {r.referee_phone}
                </Link>
              ) : (
                'No contact given'
              )}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
